<script lang="ts">
    import Icon from "../../widgets/Icon.svelte"
    import {
        mdiPlusCircleOutline,
        mdiChevronDownCircleOutline,
        mdiChevronUpCircleOutline,
        mdiInformationSymbol,
    } from "@mdi/js"
    import Rule from "./Rule.svelte"
    import ExceptionFilter from "./ExceptionFilter/ExceptionFilter.svelte"
    import FilterTitleField from "./ExceptionFilter/FilterTitleField.svelte"
    import { _ } from "svelte-i18n"
    import { tooltip } from "../../../helper"

    export let exception: PriceCalculationException
    export let priceCalculation: PriceCalculation
    let rebuiltRules = false
    function setupInitialExceptionState() {
        exception = {
            rules: [],
        }
        if (priceCalculation.ruleBasedCalculation)
            priceCalculation.ruleBasedCalculation
                .filter((rule) => !isNaN(rule.installationType))
                .forEach((rule) => {
                    if (rule && (rule.installationType || rule.installationType === 0))
                        exception.rules.push({
                            name: $_("components.pcalc.exceptionRule"),
                            filter: [
                                {
                                    attribute: "installationType",
                                    stringArrayValues: [rule.installationType.toString()],
                                    operator: "eq",
                                },
                            ],
                            type: "surcharge",
                            surchargePercent: rule.surchargePercent,
                            discountPercent: rule.discountPercent,
                        })
                })
        if (!exception.rules.length) {
            // do nothing
        }
        delete priceCalculation.ruleBasedCalculation
    }

    if (!exception) setupInitialExceptionState()

    const addRule = () => {
        if (!exception) setupInitialExceptionState()
        if (!exception.rules) exception.rules = []
        exception.rules.push({
            name: $_("components.pcalc.exceptionRule"),
            filter: [
                {
                    attribute: "installationType",
                    stringArrayValues: [],
                    operator: "eq",
                },
            ],
            type: "surcharge",
            surchargePercent: null,
            discountPercent: null,
        })
        exception.rules = [...exception.rules]
    }

    const addFilter = (rule: number) => {
        exception.rules[rule].filter.push({
            attribute: "installationType",
            stringArrayValues: [],
            operator: "eq",
        })
        exception.rules[rule].filter = [...exception.rules[rule].filter]
    }

    function moveRuleUp(index: number) {
        if (index > 0) {
            const temp = exception.rules[index - 1]
            exception.rules[index - 1] = exception.rules[index]
            exception.rules[index] = temp
            exception.rules = [...exception.rules]
            rebuiltRules = !rebuiltRules
        }
    }
    function moveRuleDown(index: number) {
        if (index < exception.rules.length - 1) {
            const temp = exception.rules[index + 1]
            exception.rules[index + 1] = exception.rules[index]
            exception.rules[index] = temp
            exception.rules = [...exception.rules]
            rebuiltRules = !rebuiltRules
        }
    }
    const minimizedRules = exception.rules.map((rule) => false)
</script>

<style lang="less">
    .exception-rules {
        .exception-rules-array {
            list-style: none;
            padding: 0;
            margin: 0;

            & > li {
                border: 1px dashed #9a9a9a;
                border-radius: 12px;
                padding: 1.2rem;
                margin: 1rem 0px;
                position: relative;
                button.remove {
                    position: absolute;
                    top: 4px;
                    right: 0px;
                    background: none;
                    border: none;
                    cursor: pointer;
                    height: 24px;
                    transform: translateX(50%);
                    background-color: white;
                }
                &.folded {
                    .title-seperator {
                        padding: 0px 0px;
                    }
                }
                .title-seperator {
                    display: flex;
                    gap: 0.5rem;
                    align-items: center;

                    span {
                        font-size: 18px;
                    }
                    .seperator {
                        flex-grow: 1;
                        border-bottom: 1px dashed #9a9a9a;
                    }
                    button {
                        background: none;
                        border: none;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                        color: var(--color-gray-600);
                    }
                    &.main {
                        .seperator {
                            flex-grow: 1;
                            border-bottom: 1px solid #9a9a9a;
                            margin: 1rem 0px;
                        }
                    }
                }
                .seperator {
                    font-size: 0.8rem;
                    color: var(--color-gray-600);
                }
                .exception-filter-array {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 0.6rem;
                    width: 100%;

                    .exception-filter-element {
                        width: 100%;
                    }
                }
                .sort {
                    display: flex;
                    flex-direction: column;
                    position: absolute;
                    top: 50%;
                    transform: translate(50%, -50%);
                    right: 0px;
                    background-color: white;
                    svg {
                        height: 24px;
                    }
                    button {
                        background: none;
                        border: none;
                        cursor: pointer;
                        display: flex;
                    }
                }
            }
        }
    }
</style>

<section class="exception-rules">
    <div class="section-title" style="display: flex; gap: 15px; align-items: flex-end;">
        {$_("components.pcalc.exceptionRules")}
        <span
            style="font-size: 10px; display: flex; align-items: flex-end; padding-bottom: 8px;"
            use:tooltip="{{
                content: $_('components.pcalc.youCanIndivualize'),
            }}"
        >
            <p style="padding:0px;margin: 0px; line-height: 20px;">
                {$_("components.pcalc.processedTopBottom")}
            </p>
            <Icon size="18px" path="{mdiInformationSymbol}" /></span
        >
    </div>

    <ul class="exception-rules-array">
        {#key rebuiltRules}
            {#each exception?.rules || [] as rule, index}
                <li class:folded="{minimizedRules[index]}">
                    {#key minimizedRules[index]}
                        <button
                            class="remove"
                            use:tooltip="{{
                                content: minimizedRules[index] ? $_('general.foldOut') : $_('general.foldIn'),
                            }}"
                            on:click|stopPropagation|preventDefault="{() => {
                                minimizedRules[index] = !minimizedRules[index]
                            }}"
                        >
                            <Icon
                                size="24px"
                                color="#9a9a9a"
                                path="{minimizedRules[index] ? mdiChevronUpCircleOutline : mdiChevronDownCircleOutline}"
                            />
                        </button>
                    {/key}
                    <div class="sort">
                        {#if index !== 0}
                            <button
                                use:tooltip="{{
                                    content: $_('general.pushUp'),
                                }}"
                                class="move-up"
                                on:click|stopPropagation|preventDefault="{() => moveRuleUp(index)}"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="36"
                                    height="37"
                                    viewBox="0 0 36 37"
                                    fill="none"
                                >
                                    <path
                                        d="M19.5 27.5H16.5V15.5L11.25 20.75L9.12 18.62L18 9.74L26.88 18.62L24.75 20.75L19.5 15.5V27.5ZM18 3.5C19.9698 3.5 21.9204 3.88799 23.7403 4.64181C25.5601 5.39563 27.2137 6.50052 28.6066 7.8934C29.9995 9.28628 31.1044 10.9399 31.8582 12.7597C32.612 14.5796 33 16.5302 33 18.5C33 22.4782 31.4196 26.2936 28.6066 29.1066C25.7936 31.9196 21.9782 33.5 18 33.5C16.0302 33.5 14.0796 33.112 12.2597 32.3582C10.4399 31.6044 8.78628 30.4995 7.3934 29.1066C4.58035 26.2936 3 22.4782 3 18.5C3 14.5218 4.58035 10.7064 7.3934 7.8934C10.2064 5.08035 14.0218 3.5 18 3.5ZM18 6.5C14.8174 6.5 11.7652 7.76428 9.51472 10.0147C7.26428 12.2652 6 15.3174 6 18.5C6 21.6826 7.26428 24.7348 9.51472 26.9853C11.7652 29.2357 14.8174 30.5 18 30.5C21.1826 30.5 24.2348 29.2357 26.4853 26.9853C28.7357 24.7348 30 21.6826 30 18.5C30 15.3174 28.7357 12.2652 26.4853 10.0147C24.2348 7.76428 21.1826 6.5 18 6.5Z"
                                        fill="#9A9A9A"
                                    ></path>
                                </svg>
                            </button>
                        {/if}
                        {#if index < exception.rules.length - 1}
                            <button
                                use:tooltip="{{
                                    content: $_('general.pushDown'),
                                }}"
                                class="move-down"
                                on:click|stopPropagation|preventDefault="{() => moveRuleDown(index)}"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="36"
                                    height="37"
                                    viewBox="0 0 36 37"
                                    fill="none"
                                >
                                    <path
                                        d="M16.5 9.5H19.5V21.5L24.75 16.25L26.88 18.38L18 27.26L9.12 18.38L11.25 16.25L16.5 21.5V9.5ZM18 33.5C16.0302 33.5 14.0796 33.112 12.2597 32.3582C10.4399 31.6044 8.78628 30.4995 7.3934 29.1066C4.58035 26.2936 3 22.4782 3 18.5C3 14.5218 4.58035 10.7064 7.3934 7.8934C10.2064 5.08035 14.0218 3.5 18 3.5C19.9698 3.5 21.9204 3.88799 23.7403 4.64181C25.5601 5.39563 27.2137 6.50052 28.6066 7.8934C29.9995 9.28628 31.1044 10.9399 31.8582 12.7597C32.612 14.5796 33 16.5302 33 18.5C33 22.4782 31.4196 26.2936 28.6066 29.1066C25.7936 31.9196 21.9782 33.5 18 33.5ZM18 30.5C21.1826 30.5 24.2348 29.2357 26.4853 26.9853C28.7357 24.7348 30 21.6826 30 18.5C30 15.3174 28.7357 12.2652 26.4853 10.0147C24.2348 7.76428 21.1826 6.5 18 6.5C14.8174 6.5 11.7652 7.76428 9.51472 10.0147C7.26428 12.2652 6 15.3174 6 18.5C6 21.6826 7.26428 24.7348 9.51472 26.9853C11.7652 29.2357 14.8174 30.5 18 30.5Z"
                                        fill="#9A9A9A"
                                    ></path>
                                </svg>
                            </button>
                        {/if}
                    </div>
                    <div class="title-seperator main">
                        <FilterTitleField
                            title="{rule.name}"
                            on:change="{(event) => {
                                exception.rules[index].name = event.detail
                            }}"
                            on:remove="{() => {
                                exception.rules = exception.rules.filter((_, i) => i !== index)
                                rebuiltRules = !rebuiltRules
                            }}"
                        />
                    </div>
                    {#if !minimizedRules[index]}
                        <div class="title-seperator main">
                            <span>{$_("components.pcalc.filter")}</span>
                            <div class="seperator mt-sm mb-xs"></div>
                        </div>

                        <ul class="exception-filter-array">
                            {#each rule.filter as filter, i}
                                <li class="exception-filter-element">
                                    <ExceptionFilter
                                        bind:filter
                                        on:remove="{() => {
                                            rule.filter = rule.filter.filter((_, index) => index !== i)
                                            rebuiltRules = !rebuiltRules
                                        }}"
                                    />
                                </li>
                                {#if i < rule.filter.length - 1}
                                    <div class="title-seperator">
                                        <div class="seperator mt-sm mb-xs"></div>
                                        <span>{$_("general.and")}</span>
                                        <div class="seperator mt-sm mb-xs"></div>
                                    </div>
                                {/if}
                            {/each}
                            <div class="title-seperator">
                                <div class="seperator mt-sm mb-xs"></div>
                                <button
                                    on:click|stopPropagation|preventDefault="{() => {
                                        addFilter(index)
                                    }}"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                    >
                                        <path
                                            d="M12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM12 2C10.6868 2 9.38642 2.25866 8.17317 2.7612C6.95991 3.26375 5.85752 4.00035 4.92893 4.92893C3.05357 6.8043 2 9.34784 2 12C2 14.6522 3.05357 17.1957 4.92893 19.0711C5.85752 19.9997 6.95991 20.7362 8.17317 21.2388C9.38642 21.7413 10.6868 22 12 22C14.6522 22 17.1957 20.9464 19.0711 19.0711C20.9464 17.1957 22 14.6522 22 12C22 10.6868 21.7413 9.38642 21.2388 8.17317C20.7362 6.95991 19.9997 5.85752 19.0711 4.92893C18.1425 4.00035 17.0401 3.26375 15.8268 2.7612C14.6136 2.25866 13.3132 2 12 2ZM13 7H11V11H7V13H11V17H13V13H17V11H13V7Z"
                                            fill="#9A9A9A"
                                        ></path>
                                    </svg>
                                </button>
                                <div class="seperator mt-sm mb-xs"></div>
                            </div>
                        </ul>
                        <div class="title-seperator main">
                            <span>{$_("general.rule")}</span>
                            <div class="seperator mt-sm mb-xs"></div>
                        </div>
                        <Rule bind:rule {index} />
                    {/if}
                </li>
            {/each}
        {/key}
    </ul>
    <div class="layout justify-content-center mt-md">
        <button type="button" class="btn btn-secondary btn-outline" on:click="{addRule}" data-cy="add-exception">
            <Icon path="{mdiPlusCircleOutline}" />
            <span>{$_("components.pcalc.addExceptionRule")}</span>
        </button>
    </div>
</section>
